import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { debounceTime, Subject } from 'rxjs';
import { ProformsAdminService } from '../../../../services/proforms/proforms-admin.service';
import { ProFormsQuestionAPIResponse } from '../../../../types/responses/proforms.responses';

export interface FieldTypeSpecs {
  minValue: number;
  minLabel: string;
  maxValue: number;
  maxLabel: string;
}

@Component({
  selector: 'app-proforms-admin-question-answer-scale',
  standalone: false,

  templateUrl: './proforms-admin-question-answer-scale.component.html',
  styleUrl: './proforms-admin-question-answer-scale.component.scss',
})
export class ProformsAdminQuestionAnswerScaleComponent
  implements OnInit, OnChanges
{
  @Input() question: ProFormsQuestionAPIResponse;

  fieldTypeSpecs: FieldTypeSpecs;

  minValueChange$ = new Subject<number>();

  maxValueChange$ = new Subject<number>();

  minLabelChange$ = new Subject<string>();

  maxLabelChange$ = new Subject<string>();

  constructor(private proformsAdminService: ProformsAdminService) {}

  ngOnInit() {
    if (!this.question?.field_type_specs) {
      this.fieldTypeSpecs = JSON.parse(
        '{"minValue":1,"minLabel":"Not at all","maxValue":5,"maxLabel":"Very much"}'
      );
    }
    this.minValueChange$.pipe(debounceTime(300)).subscribe((value) => {
      this.updateMinValue(value);
    });

    this.maxValueChange$.pipe(debounceTime(300)).subscribe((value) => {
      this.updateMaxValue(value);
    });

    this.minLabelChange$.pipe(debounceTime(300)).subscribe((label) => {
      this.updateFieldTypeSpecs({ minLabel: label });
    });

    this.maxLabelChange$.pipe(debounceTime(300)).subscribe((label) => {
      this.updateFieldTypeSpecs({ maxLabel: label });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['question'] &&
      changes['question'].currentValue !== changes['question'].previousValue
    ) {
      this.fieldTypeSpecs = JSON.parse(this.question.field_type_specs);
    }
  }

  onMinValueChange(value: number) {
    this.minValueChange$.next(value);
  }

  onMaxValueChange(value: number) {
    this.maxValueChange$.next(value);
  }

  onMinLabelChange(label: string) {
    this.minLabelChange$.next(label);
  }

  onMaxLabelChange(label: string) {
    this.maxLabelChange$.next(label);
  }

  updateMinValue(value: number) {
    const max = this.fieldTypeSpecs.maxValue;

    if (value < 0) {
      value = 0;
    }

    if (value >= max) {
      this.fieldTypeSpecs.minValue = max - 1;
    } else if (max - value > 10) {
      this.fieldTypeSpecs.minValue = max - 10;
    } else {
      this.fieldTypeSpecs.minValue = value;
    }

    this.updateFieldTypeSpecs({ minValue: this.fieldTypeSpecs.minValue });
  }

  updateMaxValue(value: number) {
    const min = this.fieldTypeSpecs.minValue;

    if (value < 0) {
      value = 0;
    }

    if (value <= min) {
      this.fieldTypeSpecs.maxValue = min + 1;
    } else if (value - min > 10) {
      this.fieldTypeSpecs.maxValue = min + 10;
    } else {
      this.fieldTypeSpecs.maxValue = value;
    }

    this.updateFieldTypeSpecs({ maxValue: this.fieldTypeSpecs.maxValue });
  }

  updateFieldTypeSpecs(changes: Partial<FieldTypeSpecs>) {
    const updated = {
      ...this.fieldTypeSpecs,
      ...changes,
    };

    const newValue = JSON.stringify(updated);

    this.proformsAdminService.updateQuestion(
      this.question.id,
      this.question.proform_form_section_id,
      { field_type_specs: newValue }
    );
  }
}
